/**
 * Trial Service
 *
 * Service functions for converting trial sessions to database projects.
 * Used when users complete the trial flow and login/signup.
 */

import { supabase } from '@/lib/supabase'
import { Phase1Data, Phase2Data, Phase3Data } from '@/types'
import {
  markTrialSessionConverted,
  getTrialSessionFromDB,
  TrialSessionError,
} from './trialSessionService'
import { normalizeProjectData } from '@/utils/normalizeProjectData'

const STORAGE_KEY_PREFIX = 'framed_trial_'

/**
 * Retry a function with exponential backoff.
 * Only retries on transient errors (network failures, 5xx responses).
 */
async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  baseDelay = 1000
): Promise<T> {
  let lastError: unknown
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn()
    } catch (err) {
      lastError = err
      if (attempt < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, attempt)
        console.warn(`[trialService] Attempt ${attempt + 1} failed, retrying in ${delay}ms...`, err)
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }
  }
  throw lastError
}

export interface TrialSessionData {
  sessionId: string
  createdAt: string
  lastUpdated: string
  currentPhase: 1 | 2 | 3
  phase1?: Phase1Data
  phase2?: Phase2Data
  phase3?: Phase3Data
}

// Error codes for trial conversion
export type TrialConversionErrorCode =
  | TrialSessionError
  | 'USER_FETCH_FAILED'
  | 'PROJECT_CREATE_FAILED'
  | 'UNEXPECTED_ERROR'

export interface SaveTrialResult {
  success: boolean
  projectId?: string
  error?: string
  errorCode?: TrialConversionErrorCode
}

/**
 * Get trial session data from localStorage
 */
function getTrialSession(sessionId: string): TrialSessionData | null {
  try {
    const storageKey = `${STORAGE_KEY_PREFIX}${sessionId}`
    const stored = localStorage.getItem(storageKey)
    if (!stored) return null
    return JSON.parse(stored) as TrialSessionData
  } catch {
    return null
  }
}

/**
 * Clear trial session from localStorage
 */
function clearTrialSession(sessionId: string): void {
  const storageKey = `${STORAGE_KEY_PREFIX}${sessionId}`
  localStorage.removeItem(storageKey)
}

/**
 * Migrate files uploaded during the trial from trial/{sessionId}/context/
 * to {projectId}/context/ in the project-media bucket.
 * Also updates file URLs in the project's phase1 context data.
 */
async function migrateTrialFiles(
  sessionId: string,
  projectId: string,
  projectData: { phase1: any; phase2: any; phase3: any; trial_file_note?: string }
): Promise<void> {
  try {
    const trialPath = `trial/${sessionId}/context`

    // List files in the trial path
    const { data: trialFiles, error: listError } = await supabase.storage
      .from('project-media')
      .list(trialPath)

    if (listError || !trialFiles || trialFiles.length === 0) {
      return // No trial files to migrate
    }


    const urlMapping: Record<string, string> = {}

    for (const file of trialFiles) {
      if (file.name === '.emptyFolderPlaceholder') continue

      const oldPath = `${trialPath}/${file.name}`
      const newPath = `${projectId}/context/${file.name}`

      // Download the file
      const { data: fileData, error: downloadError } = await supabase.storage
        .from('project-media')
        .download(oldPath)

      if (downloadError || !fileData) {
        console.warn(`[trialService] Failed to download trial file: ${oldPath}`, downloadError)
        continue
      }

      // Upload to new path
      const { error: uploadError } = await supabase.storage
        .from('project-media')
        .upload(newPath, fileData, {
          cacheControl: '3600',
          upsert: false,
        })

      if (uploadError) {
        console.warn(`[trialService] Failed to upload migrated file: ${newPath}`, uploadError)
        continue
      }

      // Build URL mapping
      const { data: { publicUrl: oldUrl } } = supabase.storage
        .from('project-media')
        .getPublicUrl(oldPath)
      const { data: { publicUrl: newUrl } } = supabase.storage
        .from('project-media')
        .getPublicUrl(newPath)

      urlMapping[oldUrl] = newUrl

      // Delete the old file
      await supabase.storage
        .from('project-media')
        .remove([oldPath])
    }

    // Update context_files URLs in the project data
    if (Object.keys(urlMapping).length > 0 && projectData.phase1?.project_context?.context_files) {
      const updatedFiles = projectData.phase1.project_context.context_files.map(
        (f: { url: string; [key: string]: any }) => ({
          ...f,
          url: urlMapping[f.url] || f.url,
        })
      )

      await supabase
        .from('projects')
        .update({
          data: {
            ...projectData,
            phase1: {
              ...projectData.phase1,
              project_context: {
                ...projectData.phase1.project_context,
                context_files: updatedFiles,
              },
            },
          },
        })
        .eq('id', projectId)

    }
  } catch (err) {
    // Don't fail the conversion if file migration fails
    console.error('[trialService] Error migrating trial files:', err)
  }
}

/**
 * Save trial session to a database project
 *
 * This function:
 * 1. Checks if trial session exists and is not already converted (atomically)
 * 2. Retrieves trial data from localStorage or database
 * 3. Creates a new project in Supabase with the user_id
 * 4. Copies phase1, phase2, phase3 data to project.data
 * 5. Marks trial as converted BEFORE returning (prevents race conditions)
 * 6. Removes the trial session from localStorage
 * 7. Returns the new project id
 *
 * @param sessionId - The trial session ID from localStorage
 * @param userId - The authenticated user's ID
 * @returns SaveTrialResult with success status and project ID or error
 */
export async function saveTrialToProject(
  sessionId: string,
  userId: string
): Promise<SaveTrialResult> {
  try {
    // 1. Get trial session data - try localStorage first, then database
    let sessionData = getTrialSession(sessionId)

    if (!sessionData) {
      // Try database as fallback - this also checks for already-converted sessions
      const dbResult = await getTrialSessionFromDB(sessionId)

      if (!dbResult.success) {
        // Return specific error based on the error code
        let errorMessage: string
        switch (dbResult.error) {
          case 'SESSION_EXPIRED':
            errorMessage =
              'Your trial session has expired. Trial sessions are valid for 90 days. Please start a new trial.'
            break
          case 'SESSION_CONVERTED':
            // If already converted, return the existing project ID
            if (dbResult.convertedProjectId) {
              return {
                success: true,
                projectId: dbResult.convertedProjectId,
              }
            }
            errorMessage = 'This trial has already been converted to a project.'
            break
          case 'SESSION_NOT_FOUND':
            errorMessage = 'Trial session not found. It may have expired or never existed.'
            break
          default:
            errorMessage = 'Unable to retrieve trial session. Please try again.'
        }

        return {
          success: false,
          error: errorMessage,
          errorCode: dbResult.error,
        }
      }

      // Session found in database
      const dbSession = dbResult.data!
      sessionData = {
        sessionId: dbSession.session_id,
        createdAt: dbSession.created_at,
        lastUpdated: dbSession.updated_at,
        currentPhase: dbSession.current_phase as 1 | 2 | 3,
        phase1: dbSession.phase1_data || undefined,
        phase2: dbSession.phase2_data || undefined,
        phase3: dbSession.phase3_data || undefined,
      }
    } else {
      // Session found in localStorage - still check DB for conversion status
      // This prevents race conditions when multiple devices/tabs try to convert
      const dbResult = await getTrialSessionFromDB(sessionId)

      if (dbResult.success === false && dbResult.error === 'SESSION_CONVERTED') {
        if (dbResult.convertedProjectId) {
          clearTrialSession(sessionId)
          return {
            success: true,
            projectId: dbResult.convertedProjectId,
          }
        }
      }
    }

    if (!sessionData) {
      return {
        success: false,
        error: 'Trial session not found or expired',
        errorCode: 'SESSION_NOT_FOUND',
      }
    }

    // 2. Ensure user has a personal account (creates one if needed)
    let userAgencyId: string | null = null
    let userRole: string | null = null

    // First, get user's current data
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('agency_id, role')
      .eq('id', userId)
      .single()

    if (userError) {
      console.error('[trialService] Error fetching user:', userError)
      // Don't continue without knowing if user exists - this is critical
      if (userError.code === 'PGRST116') {
        return {
          success: false,
          error: 'User account not found. Please try logging in again.',
          errorCode: 'USER_FETCH_FAILED',
        }
      }
      // For other errors, proceed with creating a personal account
    } else {
      userAgencyId = userData?.agency_id
      userRole = userData?.role
    }

    // If user has no agency, create a personal account for them
    if (!userAgencyId) {

      // Call the database function to create a personal account
      const { data: newAgencyId, error: createError } = await supabase.rpc(
        'create_personal_account_for_user',
        { p_user_id: userId }
      )

      if (createError) {
        console.error('[trialService] Error creating personal account:', createError)
        return {
          success: false,
          error: 'Failed to create your account. Please try again.',
          errorCode: 'PROJECT_CREATE_FAILED',
        }
      }

      userAgencyId = newAgencyId

      // Refresh user data to get updated role (should be 'client' by default)
      const { data: refreshedUser } = await supabase
        .from('users')
        .select('role')
        .eq('id', userId)
        .single()

      if (refreshedUser) {
        userRole = refreshedUser.role
      }
    }

    // 3. Prepare project data
    const projectName = sessionData.phase1?.business_name || 'My Trial Project'

    // Check if there are any base64 images from trial uploads
    const hasTrialFiles = sessionData.phase1?.logo_url?.startsWith('data:')

    // Add trial file note if there are embedded files
    const trialFileNote = hasTrialFiles
      ? 'Note: This project contains files that were uploaded during the trial period. ' +
        'These files are stored as base64 data within the project. ' +
        'When building, the AI should import these files into its own project storage.'
      : undefined

    const projectData = {
      phase1: sessionData.phase1 || {},
      phase2: sessionData.phase2 || { pages: [] },
      phase3: sessionData.phase3 || {},
      trial_file_note: trialFileNote,
    }

    // Normalize trial data before creating the project — trial sessions
    // can have incomplete/malformed data (empty objects, null array entries)
    normalizeProjectData(projectData)

    // 4. Create project in database
    // User should now have a personal account
    const projectAgencyId = userAgencyId!

    let project: { id: string }
    try {
      project = await withRetry(async () => {
        const { data, error: insertError } = await supabase
          .from('projects')
          .insert({
            name: projectName,
            status: 'draft',
            project_type: 'standard',
            current_step: sessionData.currentPhase || 1,
            data: projectData,
            user_id: userId,
            created_by: userId,
            agency_id: projectAgencyId,
            enabled_modules: ['build'],
          })
          .select('id')
          .single()

        if (insertError) throw insertError
        return data
      })
    } catch (insertError: any) {
      console.error('[trialService] Error creating project after retries:', insertError)
      return {
        success: false,
        error: insertError.message || 'Failed to create project',
        errorCode: 'PROJECT_CREATE_FAILED',
      }
    }

    // 5. Mark trial session as converted IMMEDIATELY (before adding member)
    // This prevents race conditions where another device tries to convert
    const { error: convertError } = await markTrialSessionConverted(
      sessionId,
      userId,
      project.id
    )
    if (convertError) {
      console.warn('[trialService] Failed to mark session converted in DB:', convertError)
      // Continue anyway - project was created
    }

    // 6. Add user as project member with appropriate role
    // Determine the member role based on user's actual role
    let memberRole: 'client' | 'manager' = 'client'
    if (userRole === 'manager') {
      memberRole = 'manager'
    }
    // super_admin and admin don't need project_members record - they have full access via RLS

    const shouldAddMember = userRole !== 'super_admin' && userRole !== 'admin'

    if (shouldAddMember) {
      const { error: memberError } = await supabase.from('project_members').insert({
        project_id: project.id,
        user_id: userId,
        role: memberRole,
      })

      if (memberError) {
        // Log but don't fail - project was created successfully
        console.warn('[trialService] Error adding project member:', memberError)
      }
    }

    // 7. Migrate trial upload files to the real project path
    await migrateTrialFiles(sessionId, project.id, projectData)


    // 8. Clear trial session from localStorage ONLY after confirmed success
    clearTrialSession(sessionId)

    return {
      success: true,
      projectId: project.id,
    }
  } catch (err) {
    console.error('[trialService] Unexpected error:', err)
    return {
      success: false,
      error: 'An unexpected error occurred while saving your project',
      errorCode: 'UNEXPECTED_ERROR',
    }
  }
}
